<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-lg-12">
        <form @submit.prevent="saveSchedule()" autocomplete="off">
          <!-- Header Card with Actions -->
          <div class="card">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <h4 class="card-title mb-1">{{ schedule.id ? 'Editar Programação' : 'Nova Programação' }}</h4>
                  <p class="text-muted mb-0">Preencha as informações da programação</p>
                </div>
                <div>
                  <button type="button" @click="cancelar()" class="btn btn-outline-secondary mr-2" :disabled="saving">
                    <i class="bx bx-x mr-1"></i> Cancelar
                  </button>
                  <button type="submit" class="btn btn-primary" :disabled="saving">
                    <i class="bx bx-save mr-1"></i> {{ saving ? 'Salvando...' : 'Salvar' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Schedule Info Section -->
          <div class="card">
            <div class="card-body">
              <h5 class="card-title mb-4">
                <i class="bx bx-calendar text-primary mr-2"></i>
                Informações da Programação
              </h5>
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>
                      Sala
                      <span class="text-danger">*</span>
                    </label>
                    <room-select
                      :value="roomSelected"
                      @input="setRoom"
                      :class="{ 'is-invalid': submitted && $v.schedule.room_id.$error }"
                      :classError="$v.schedule.room_id.$error ? true : false"
                    ></room-select>
                    <div v-if="submitted && !$v.schedule.room_id.required" class="invalid-feedback">
                      O campo sala é obrigatório.
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form-group">
                    <label>
                      Data
                      <span class="text-danger">*</span>
                    </label>
                    <date-picker
                      v-model="schedule.schedule_date"
                      :first-day-of-week="1"
                      lang="pt-br"
                      format="DD/MM/YYYY"
                      :class="{ 'is-invalid': submitted && $v.schedule.schedule_date.$error }"
                    ></date-picker>
                    <div v-if="submitted && !$v.schedule.schedule_date.required" class="invalid-feedback">
                      O campo data é obrigatório.
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form-group">
                    <label>
                      Exibição
                      <span class="text-danger">*</span>
                    </label>
                    <select
                      v-model="schedule.display"
                      class="form-control"
                      :class="{ 'is-invalid': submitted && $v.schedule.display.$error }"
                    >
                      <option value="1">Web</option>
                      <option value="0">App</option>
                      <option value="10">Web e App</option>
                    </select>
                    <div v-if="submitted && !$v.schedule.display.required" class="invalid-feedback">
                      O campo Exibição é obrigatório.
                    </div>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-3">
                  <div class="form-group">
                    <label>
                      Horário de Início
                      <span class="text-danger">*</span>
                    </label>
                    <date-picker
                      v-model="schedule.start_time"
                      format="HH:mm"
                      value-type="format"
                      :showSecond="false"
                      type="time"
                      placeholder="HH:mm"
                      :class="{ 'is-invalid': submitted && $v.schedule.start_time.$error }"
                    ></date-picker>
                    <div v-if="submitted && !$v.schedule.start_time.required" class="invalid-feedback">
                      O campo horário de início é obrigatório.
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form-group">
                    <label>
                      Horário de Término
                      <span class="text-danger">*</span>
                    </label>
                    <date-picker
                      v-model="schedule.end_time"
                      format="HH:mm"
                      :showSecond="false"
                      value-type="format"
                      type="time"
                      placeholder="HH:mm"
                      :class="{ 'is-invalid': submitted && $v.schedule.end_time.$error }"
                    ></date-picker>
                    <div v-if="submitted && !$v.schedule.end_time.required" class="invalid-feedback">
                      O campo horário de término é obrigatório.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Translatable Content Section -->
          <div class="card">
            <div class="card-body">
              <h5 class="card-title mb-4">
                <i class="bx bx-world text-primary mr-2"></i>
                Conteúdo da Programação
                <span v-if="isMultiLanguage" class="badge badge-soft-info ml-2">Multi-idioma</span>
              </h5>

              <!-- Single Language Mode -->
              <div v-if="!isMultiLanguage">
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>
                        Título
                        <span class="text-danger">*</span>
                      </label>
                      <input
                        v-model="schedule.title"
                        type="text"
                        class="form-control"
                        placeholder="Título da programação"
                        :class="{ 'is-invalid': submitted && $v.schedule.title.$error }"
                      />
                      <div v-if="submitted && !$v.schedule.title.required" class="invalid-feedback">
                        O campo título é obrigatório.
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>
                        Tipo
                        <span class="text-danger">*</span>
                      </label>
                      <select
                        v-model="schedule.schedule_type"
                        class="form-control"
                        :class="{ 'is-invalid': submitted && $v.schedule.schedule_type.$error }"
                      >
                        <option value="">Selecione uma opção</option>
                        <option v-for="opt in scheduleTypeOptions['default']" :key="opt" :value="opt">{{ opt }}</option>
                      </select>
                      <div v-if="submitted && !$v.schedule.schedule_type.required" class="invalid-feedback">
                        O campo tipo é obrigatório.
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <div class="form-group">
                      <label>Descrição</label>
                      <textarea
                        v-model="schedule.description"
                        class="form-control"
                        rows="4"
                        placeholder="Descrição da programação..."
                      ></textarea>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Multi Language Mode -->
              <div v-else>
                <b-tabs v-model="selectedLanguageTab" content-class="mt-3" nav-class="nav-tabs-custom">
                  <b-tab
                    v-for="(lang, index) in activeLanguages"
                    :key="lang.id"
                    :active="index === 0"
                  >
                    <template #title>
                      <span class="d-flex align-items-center">
                        <span v-if="lang.flag" class="mr-1">{{ lang.flag }}</span>
                        {{ lang.name || lang.native_name }}
                        <span v-if="lang.is_default" class="badge badge-soft-primary ml-1">Padrão</span>
                      </span>
                    </template>

                    <div class="row">
                      <div class="col-md-6">
                        <div class="form-group">
                          <label>
                            Título
                            <span v-if="lang.is_default" class="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            class="form-control"
                            :placeholder="`Título em ${lang.native_name}`"
                            :value="getTranslationValue(lang.id, 'title')"
                            @input="setTranslationValue(lang.id, 'title', $event.target.value)"
                            :class="{ 'is-invalid': submitted && lang.is_default && $v.schedule.title.$error }"
                          />
                          <div v-if="submitted && lang.is_default && !$v.schedule.title.required" class="invalid-feedback">
                            O campo título é obrigatório.
                          </div>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="form-group">
                          <label>
                            Tipo
                            <span v-if="lang.is_default" class="text-danger">*</span>
                          </label>
                          <select
                            class="form-control"
                            :value="getTranslationValue(lang.id, 'schedule_type')"
                            @change="setTranslationValue(lang.id, 'schedule_type', $event.target.value)"
                            :class="{ 'is-invalid': submitted && lang.is_default && $v.schedule.schedule_type.$error }"
                          >
                            <option value="">Selecione uma opção</option>
                            <option v-for="opt in getScheduleTypeOptionsForLang(lang.code)" :key="opt" :value="opt">{{ opt }}</option>
                          </select>
                          <div v-if="submitted && lang.is_default && !$v.schedule.schedule_type.required" class="invalid-feedback">
                            O campo tipo é obrigatório.
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-12">
                        <div class="form-group">
                          <label>Descrição</label>
                          <textarea
                            class="form-control"
                            rows="4"
                            :placeholder="`Descrição em ${lang.native_name}`"
                            :value="getTranslationValue(lang.id, 'description')"
                            @input="setTranslationValue(lang.id, 'description', $event.target.value)"
                          ></textarea>
                        </div>
                      </div>
                    </div>
                  </b-tab>
                </b-tabs>
              </div>
            </div>
          </div>

          <!-- Speakers Section -->
          <div class="card">
            <div class="card-body">
              <h5 class="card-title mb-4">
                <i class="bx bx-user-voice text-primary mr-2"></i>
                Palestrantes
              </h5>
              <div class="row">
                <div class="list-speakers" v-for="(ls, k) in speakersList" :key="k">
                  <span class="speaker-avatar">
                    <img :src="ls.photo" class="rounded avatar-xl">
                  </span>
                  <p class="text-center font-size-9 mb-0 mt-1">{{ ls.name }}</p>
                </div>
                <div class="list-speakers add-speaker" @click="modalSpeakers()">
                  <div class="add-speaker-btn">
                    <img :src="avatar" class="rounded avatar-xl">
                    <p>Adicionar/Remover</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Mobile Save Button -->
          <div class="card d-md-none">
            <div class="card-body">
              <div class="d-flex justify-content-end">
                <button type="button" @click="cancelar()" class="btn btn-outline-secondary mr-2" :disabled="saving">
                  Cancelar
                </button>
                <button type="submit" class="btn btn-primary" :disabled="saving">
                  {{ saving ? 'Salvando...' : 'Salvar' }}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
    <modal-palestrantes :selecteds="speakersList" ref="modalPalestrantes" @success="defineSpeakers"></modal-palestrantes>
  </Layout>
</template>

<script>
import DatePicker from 'vue2-datepicker'
import Moment from 'moment'
import 'vue2-datepicker/locale/pt-br'
import Layout from '@/components/layout/vertical'
import ModalPalestrantes from '@/components/widgets/modal-palestrantes'
import PageHeader from '@/components/page-header'
import appConfig from '@/app.config'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import { mapActions, mapState } from 'vuex'
import { required } from 'vuelidate/lib/validators'
import roomSelect from '@/components/selects/room-select'
import translatableMixin from '@/mixins/translatableMixin'

export default {
  components: {
    roomSelect,
    DatePicker,
    Layout,
    ModalPalestrantes,
    PageHeader
  },
  mixins: [translatableMixin],
  name: 'Programação',
  page: {
    title: 'Programação',
    meta: [{
      name: 'description',
      content: appConfig.description
    }]
  },
  data () {
    return {
      autoClose: '',
      schedule: {
        id: '',
        title: '',
        description: '',
        schedule_date: '',
        start_time: '',
        end_time: '',
        schedule_type: '',
        room_id: '',
        display: '1'
      },
      roomSelected: {},
      rooms: [],
      speakersList: [],
      searchSpeark: '',
      avatar: require('@/assets/images/users/user-default.png'),
      submitted: false,
      saving: false,
      selectedLanguageTab: 0,
      scheduleTypeOptions: {
        'default': [
          'Premiação',
          'Abertura',
          'Almoço',
          'Apresentação',
          'Apresentação Artística',
          'Atividade Prática',
          'Aula',
          'Coffee break',
          'Competição',
          'Conferência',
          'Coordenação da atividade',
          'Credenciamento',
          'Curso',
          'Encerramento',
          'Encontro com especialista',
          'Estudo de Caso',
          'Exposição',
          'Feira',
          'Festa',
          'Fórum',
          'Imersão',
          'Intervalo',
          'Jantar',
          'Mesa Temática',
          'Mesa-redonda',
          'Oficina',
          'Painel',
          'Palestra',
          'Palestra Sessão Internacional',
          'Palestra Sessão Nacional',
          'Relatos de Experiência',
          'Roda de Conversa',
          'Seminário',
          'Sessão de Perguntas',
          'Sessão de Vídeos',
          'Sessão Solene',
          'Sessão técnica',
          'Visita técnica',
          'Webinário',
          'Workshop'
        ],
        'pt-BR': [
          'Premiação',
          'Abertura',
          'Almoço',
          'Apresentação',
          'Apresentação Artística',
          'Atividade Prática',
          'Aula',
          'Coffee break',
          'Competição',
          'Conferência',
          'Coordenação da atividade',
          'Credenciamento',
          'Curso',
          'Encerramento',
          'Encontro com especialista',
          'Estudo de Caso',
          'Exposição',
          'Feira',
          'Festa',
          'Fórum',
          'Imersão',
          'Intervalo',
          'Jantar',
          'Mesa Temática',
          'Mesa-redonda',
          'Oficina',
          'Painel',
          'Palestra',
          'Palestra Sessão Internacional',
          'Palestra Sessão Nacional',
          'Relatos de Experiência',
          'Roda de Conversa',
          'Seminário',
          'Sessão de Perguntas',
          'Sessão de Vídeos',
          'Sessão Solene',
          'Sessão técnica',
          'Visita técnica',
          'Webinário',
          'Workshop'
        ],
        'en-US': [
          'Awards',
          'Opening',
          'Lunch',
          'Presentation',
          'Artistic Presentation',
          'Practical Activity',
          'Class',
          'Coffee break',
          'Competition',
          'Conference',
          'Activity Coordination',
          'Registration',
          'Course',
          'Closing',
          'Expert Meeting',
          'Case Study',
          'Exhibition',
          'Fair',
          'Party',
          'Forum',
          'Immersion',
          'Break',
          'Dinner',
          'Thematic Table',
          'Roundtable',
          'Panel',
          'Lecture',
          'International Session Lecture',
          'National Session Lecture',
          'Experience Reports',
          'Conversation Circle',
          'Seminar',
          'Q&A Session',
          'Video Session',
          'Solemn Session',
          'Technical Session',
          'Technical Visit',
          'Webinar',
          'Workshop'
        ],
        'es-ES': [
          'Premiación',
          'Apertura',
          'Almuerzo',
          'Presentación',
          'Presentación Artística',
          'Actividad Práctica',
          'Clase',
          'Coffee break',
          'Competición',
          'Conferencia',
          'Coordinación de actividad',
          'Acreditación',
          'Curso',
          'Clausura',
          'Encuentro con especialista',
          'Estudio de Caso',
          'Exposición',
          'Feria',
          'Fiesta',
          'Foro',
          'Inmersión',
          'Intervalo',
          'Cena',
          'Mesa Temática',
          'Mesa redonda',
          'Taller',
          'Panel',
          'Conferencia',
          'Conferencia Sesión Internacional',
          'Conferencia Sesión Nacional',
          'Relatos de Experiencia',
          'Rueda de Conversación',
          'Seminario',
          'Sesión de Preguntas',
          'Sesión de Videos',
          'Sesión Solemne',
          'Sesión técnica',
          'Visita técnica',
          'Webinario',
          'Workshop'
        ]
      },
      title: 'Programação',
      items: [{
        text: 'Programação',
        href: '/schedules'
      },
      {
        text: 'Nova Programação',
        active: true
      }
      ]
    }
  },
  validations: {
    schedule: {
      room_id: { required },
      schedule_date: { required },
      display: { required },
      title: { required },
      schedule_type: { required },
      start_time: { required },
      end_time: { required }
    }
  },
  async mounted () {
    await this.loadTranslationSettings()
    this.loadItem()
  },
  computed: {
    ...mapState('schedule', { data: 'schedule' })
  },
  methods: {
    ...mapActions('schedule', {
      fetch: 'GetSchedule',
      postData: 'SaveSchedule'
    }),
    modalSpeakers () {
      this.$refs.modalPalestrantes.open()
    },
    defineSpeakers (data) {
      this.speakersList = [...data]
    },
    setRoom (room) {
      this.schedule.room_id = room
    },
    cancelar () {
      this.$router.push({ name: 'schedules' })
    },
    getScheduleTypeOptionsForLang (langCode) {
      if (this.scheduleTypeOptions[langCode]) {
        return this.scheduleTypeOptions[langCode]
      }
      const prefix = langCode ? langCode.split('-')[0] : ''
      if (prefix && this.scheduleTypeOptions[prefix]) {
        return this.scheduleTypeOptions[prefix]
      }
      return this.scheduleTypeOptions['default']
    },
    getTranslationValue (languageId, field) {
      if (languageId === this.defaultLanguageId) {
        if (this.translationData[languageId] && this.translationData[languageId][field]) {
          return this.translationData[languageId][field]
        }
        return this.schedule[field] || ''
      }
      if (this.translationData[languageId] && this.translationData[languageId][field]) {
        return this.translationData[languageId][field]
      }
      return ''
    },
    setTranslationValue (languageId, field, value) {
      if (!this.translationData[languageId]) {
        this.$set(this.translationData, languageId, {})
      }
      this.$set(this.translationData[languageId], field, value)

      if (languageId === this.defaultLanguageId) {
        this.schedule[field] = value
      }
    },
    async loadItem () {
      if (this.$route.params.id !== undefined) {
        let loader = this.$loading.show()
        this.items[1].text = 'Editar Programação'
        await this.fetch(this.$route.params.id)
        this.schedule = {
          ...this.data,
          title: this.data.title || '',
          description: this.data.description || '',
          schedule_type: this.data.schedule_type || ''
        }
        this.schedule.schedule_date = Moment(this.schedule.schedule_date, 'YYYY-MM-DD').toDate()
        this.roomSelected = this.data.room_id
        if (this.data.participants && this.data.participants.data) {
          this.defineSpeakers(this.data.participants.data)
        }
        // Load translations for this schedule
        if (this.isMultiLanguage) {
          await this.loadTranslations('schedule', this.$route.params.id)
        }
        loader.hide()
        return
      }
      // Initialize translations for new schedule
      if (this.isMultiLanguage) {
        this.initializeTranslations(['title', 'description', 'schedule_type'])
      }
    },
    async saveSchedule () {
      if (this.saving) {
        return
      }

      this.submitted = true
      this.$v.schedule.$touch()
      if (this.$v.schedule.$invalid) {
        window.scroll(0, 0)
        return
      }
      if (this.speakersList.length === 0) {
        errorMessage('Alerta: Você precisa vincular ao menos um palestrante para essa programação.', 3000)
        return
      }

      this.saving = true
      let loader = this.$loading.show()

      try {
        const scheduleData = {
          id: this.schedule.id,
          room_id: this.schedule.room_id,
          display: this.schedule.display,
          schedule_date: this.schedule.schedule_date,
          start_time: this.schedule.start_time,
          end_time: this.schedule.end_time,
          speakers: [...this.speakersList],
          translations: this.translationData
        }

        const res = await this.postData(scheduleData)

        let scheduleId = this.schedule.id
        if (res && res.data && res.data.data && res.data.data.id) {
          scheduleId = res.data.data.id
        } else if (res && res.data && res.data.id) {
          scheduleId = res.data.id
        }

        if (this.isMultiLanguage && scheduleId) {
          await this.saveTranslations('schedule', scheduleId)
        }

        let message = 'Sucesso: Dados armazenados com sucesso.'
        let delay = 2500
        successMessage(message, delay)
        this.cancelar()
        loader.hide()
      } catch (err) {
        console.error('Error saving schedule:', err)
        errorMessage('Alerta: ' + (err.message || 'Erro ao salvar programação'))
        loader.hide()
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
/* Card Styles */
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: none;
  margin-bottom: 1.5rem;
}

.card-title {
  display: flex;
  align-items: center;
  margin-bottom: 0;
}

.card-title i {
  font-size: 1.25rem;
}

/* Badge Styles */
.badge-soft-info {
  background-color: rgba(85, 110, 230, 0.1);
  color: #556ee6;
}

.badge-soft-primary {
  background-color: rgba(85, 110, 230, 0.18);
  color: #556ee6;
  font-size: 0.65rem;
  padding: 0.15rem 0.4rem;
}

/* Speakers Section */
.list-speakers {
  padding: 5px;
  text-align: center;
}

.speaker-avatar {
  padding: 4px;
  display: inline-flex;
  background: #f1f1f1;
  border-radius: 0.25rem;
}

.add-speaker {
  cursor: pointer;
}

.add-speaker-btn {
  padding: 5px;
  max-height: 150px;
  max-width: 150px;
  border: 2px dashed #e9ecef;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
}

.add-speaker-btn:hover {
  border-color: #556ee6;
}

.add-speaker-btn p {
  margin: 5px 0 0 0;
  font-size: 0.8rem;
  color: #6c757d;
}

/* Date Picker Validation */
.mx-datepicker.is-invalid {
  border: 1px solid #f46a6a !important;
  border-radius: 4px !important;
}
</style>
